import React, { useCallback, useEffect } from 'react';
import { usePentaRq, type components } from '@penta/penta-api';
import {
  Spinner,
  NotificationCard,
  Alert,
  EmptyState,
  Button,
  type NotificationCardVariant,
} from '@penta/penta-shared';
import { useTranslation } from 'react-i18next';
import { useRedirectToOldIb } from '../../hooks/useRedirectToOldIb';
import { useClient } from '../../contexts/ClientContext';
import styles from './MessageList.module.scss';

type MessageType = components['schemas']['MessageType'];
type Message = components['schemas']['Message'];

export const MessageList: React.FC = () => {
  const { t } = useTranslation();
  const api = usePentaRq();
  const { redirect: redirectToOldIb } = useRedirectToOldIb();
  const { selectedClientId } = useClient();

  const { data, isPending, error, mutate } = api.useMutation(
    'post',
    '/message/paged',
    {},
  );

  const fetchMessages = useCallback(() => {
    mutate({
      params: {
        query: {
          pageSize: 5,
          pageIndex: 0,
        },
      },
      body: {
        isHidden: false,
      },
    });
  }, [mutate]);

  useEffect(() => {
    if (selectedClientId !== null) {
      fetchMessages();
    }
  }, [fetchMessages, selectedClientId]);

  const messages = data || [];
  const isLoading = isPending;

  const getVariant = (type: MessageType): NotificationCardVariant => {
    switch (type) {
      case 'PERSONAL':
        return 'info';
      case 'INFORMATION':
        return 'warning';
      case 'BUSINESS':
        return 'error';
      case 'MARKETING':
        return 'success';
      default:
        return 'info';
    }
  };

  const getIcon = (type: MessageType): string => {
    switch (type) {
      case 'MARKETING':
        return 'megaphone';
      case 'BUSINESS':
        return 'briefcase';
      case 'INFORMATION':
        return 'information-circle';
      case 'PERSONAL':
        return 'user';
      default:
        return 'envelope';
    }
  };

  return isLoading ? (
    <div className={styles['empty-state']}>
      <Spinner />
    </div>
  ) : error ? (
    <div className={styles['error-state']}>
      <Alert variant="error" data-testid="error-messages">
        <strong>{t('messages.error.title')}</strong>
        <span>{t('messages.error.description')}</span>
      </Alert>
      <Button data-testid="retry-button" onClick={fetchMessages}>
        {t('messages.error.retry')}
      </Button>
    </div>
  ) : messages.length === 0 ? (
    <EmptyState
      icon
      title={t('messages.empty.title')}
      description={t('messages.empty.description')}
      data-testid="messages-empty-state"
    />
  ) : (
    <div className={styles.notificationsContainer}>
      {messages.map((msg: Message) => (
        <NotificationCard
          key={msg.id}
          testId={`message-${msg.id}`}
          title={t(`messages.type.${msg.messageType}`)}
          content={
            msg.header.length > 50
              ? `${msg.header.substring(0, 50)}...`
              : msg.header
          }
          variant={getVariant(msg.messageType)}
          icon={getIcon(msg.messageType)}
          onClick={() => redirectToOldIb('MessagesFromBank')}
        />
      ))}
    </div>
  );
};
